/**
 * 🌟 TÉMOIGNAGES GOOGLE - VRAIS AVIS 5/5
 * Avis réels et vérifiables sur Google
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Star, ExternalLink, MessageCircle } from 'lucide-react';

// VRAIS avis Google - 5.0/5 étoiles - 26 avis
const GOOGLE_REVIEWS = [
  {
    name: 'Sophie',
    initial: 'S',
    badge: 'COUP DE CŒUR',
    text: 'Merci à vous d\'être toujours au top ! 💫 C\'est vraiment un plaisir de faire appel à vous, vous êtes **bien plus qu\'une simple épicerie** à nos yeux; votre gentillesse, votre empathie et votre bienveillance exceptionnelle, nous offrent un **vrai service humain inégalable**. Vous êtes des commerçants comme on n\'en voit plus ! Vous avez toute notre confiance et notre soutien 🙏',
    featured: true
  },
  {
    name: 'Ynn Wll',
    initial: 'Y',
    text: 'Une épicerie de quartier qui **dynamise le quotidien** ! Prix très intéressants, promotions régulières. Service de livraison rapide et efficace. **Je recommande sans hésiter !**'
  },
  {
    name: 'RBL',
    initial: 'R',
    text: 'Je recommande. Le seul magasin où je trouve ma boisson préférée, l\'eau aromatisée à la pêche, **c\'est juste une dinguerie !** 🍑'
  },
  {
    name: 'Pikafury',
    initial: 'P',
    text: 'Superbe épicerie, **jamais déçu** ! Personnel serviable et agréable. Énormément de choix. **Je vous la recommande fortement** ❤️'
  },
  {
    name: 'Rudy Krol',
    initial: 'R',
    text: 'Super épicerie, offre complète et **accueil sympathique**. Génial ! ❤️'
  }
];

const MINI_REVIEWS = [
  { name: 'Juliette', text: 'Meilleure épicerie !' },
  { name: 'Steven', text: 'Toujours au top' },
  { name: 'Claire', text: 'Super sympa !' },
  { name: 'Martin', text: 'Excellent' },
  { name: 'Lukas', text: 'Rapide !' }
];

const ReviewCard = ({ review, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.1 }}
    className={`p-5 rounded-2xl ${review.featured ? 'col-span-full bg-gradient-to-r from-amber-500/10 to-yellow-500/5 border-2 border-amber-500/30' : 'bg-white/5 border border-white/10'}`}
  >
    <div className="flex items-start gap-4">
      {/* Avatar */}
      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${review.featured ? 'bg-gradient-to-br from-amber-500 to-yellow-500' : 'bg-gradient-to-br from-cyan-500 to-purple-500'}`}>
        {review.initial}
      </div>
      
      <div className="flex-1">
        {/* Header */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-white font-semibold">{review.name}</span>
          <img src="https://www.google.com/images/branding/googleg/1x/googleg_standard_color_128dp.png" alt="Google" className="w-4 h-4" />
          {review.badge && (
            <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold">
              ⭐ {review.badge}
            </span>
          )}
        </div>
        
        {/* 5 étoiles */}
        <div className="flex gap-0.5 mb-2">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
          ))}
        </div>
        
        {/* Text */}
        <p className="text-gray-300 text-sm leading-relaxed" dangerouslySetInnerHTML={{ 
          __html: review.text.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>') 
        }} />
      </div>
    </div>
  </motion.div>
);

const TestimonialsSection = () => {
  return (
    <section className="relative py-20 px-4">
      {/* Header */}
      <div className="text-center mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-white/5 border border-white/10 mb-6"
        >
          <img src="https://www.google.com/images/branding/googleg/1x/googleg_standard_color_128dp.png" alt="Google" className="w-8 h-8" />
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="text-3xl font-black text-white">5.0</span>
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
            </div>
            <span className="text-gray-400 text-sm">26 avis • Note parfaite !</span>
          </div>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-4xl md:text-5xl font-black text-white mb-4"
        >
          💬 Vos témoignages nous vont <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-400">droit au cœur</span> ❤️
        </motion.h2>
      </div>

      {/* Reviews Grid */}
      <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
        {GOOGLE_REVIEWS.map((review, index) => (
          <ReviewCard key={index} review={review} index={index} />
        ))}
      </div>

      {/* Mini reviews défilant */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="mt-8 overflow-hidden"
      >
        <div className="flex gap-4 justify-center flex-wrap">
          {MINI_REVIEWS.map((review, i) => (
            <span key={i} className="text-gray-400 text-sm">
              ★ {review.name}: "{review.text}"
            </span>
          ))}
        </div>
      </motion.div>

      {/* Link to Google */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="text-center mt-6"
      >
        <a 
          href="https://www.google.com/search?q=la+petite+epicerie+vermelles+avis"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-medium"
        >
          Voir tous les avis sur Google <ExternalLink className="w-4 h-4" />
        </a>
      </motion.div>
    </section>
  );
};

export default TestimonialsSection;
